/**
 * Industry presets — starting points for the intake wizard.
 *
 * KPI `key` values are STORAGE SLOTS, not names. The five slots
 * (downtime/defect/inventory/cost/custSat) are historical — the first simulation was a
 * factory — and they are the only keys a decision card can carry. Meaning comes from
 * `label`/`unit`/`direction`/`cap`, so slot `downtime` legitimately holds "Wait time" in
 * a hospital. See METRIC_SLOTS in backend/src/services/simulationGenerationService.js.
 *
 * These presets previously used domain-named keys ('waitTime', 'revenue', …). No card
 * could ever carry those, so every lookup returned undefined and the board never moved
 * for any preset other than manufacturing.
 *
 * These exist to make the "trainer types one sentence and hits generate" path
 * produce something sane. They are SUGGESTIONS: every function and every KPI is
 * editable, and Gemini refines them against the trainer's actual brief in phase 4.
 *
 * They also prove out the schema's central claim — that `functions` and `kpis` are
 * declared per simulation rather than hardcoded. Nothing outside this file changes
 * when a hospital simulation replaces a plant one.
 *
 * The manufacturing preset is lifted from the reference simulation so a trainer in
 * that domain lands on the exact shape their colleague already authored by hand.
 */

export const INDUSTRY_PRESETS = [
  {
    key: 'manufacturing',
    label: 'Manufacturing / Plant operations',
    functionNoun: 'Function',
    eventDeckLabel: 'Customer Pressure Tactic',
    functions: [
      { key: 'production',  name: 'Production',  variant: 'blue',    mandate: 'Hit the output target.',        measuredOn: ['downtime'] },
      { key: 'maintenance', name: 'Maintenance', variant: 'violet',  mandate: 'Keep the machines alive.',      measuredOn: ['downtime'] },
      { key: 'quality',     name: 'Quality',     variant: 'emerald', mandate: 'Nothing defective ships.',      measuredOn: ['defect'] },
      { key: 'logistics',   name: 'Logistics',   variant: 'amber',   mandate: 'The truck leaves on time.',     measuredOn: ['inventory', 'custSat'] },
    ],
    kpis: [
      { key: 'downtime',  label: 'Downtime',              unit: 'minutes',           direction: 'lower',   cap: 120, normaliseAbs: false, variant: 'rose'    },
      { key: 'defect',    label: 'Defects',               unit: 'percentage points', direction: 'lower',   cap: 30,  normaliseAbs: false, variant: 'amber'   },
      { key: 'inventory', label: 'Inventory buffer',      unit: 'hours',             direction: 'neutral', cap: 48,  normaliseAbs: true,  variant: 'violet'  },
      { key: 'cost',      label: 'Cost',                  unit: "₹ '000",            direction: 'lower',   cap: 200, normaliseAbs: false, variant: 'slate'   },
      { key: 'custSat',   label: 'Customer satisfaction', unit: 'points',            direction: 'higher',  cap: 20,  normaliseAbs: false, variant: 'emerald' },
    ],
  },
  {
    key: 'healthcare',
    label: 'Hospital / Clinical operations',
    functionNoun: 'Department',
    eventDeckLabel: 'External Pressure',
    functions: [
      { key: 'emergency', name: 'Emergency',   variant: 'rose',    mandate: 'Clear the department.',       measuredOn: ['downtime'] },
      { key: 'wards',     name: 'Wards',       variant: 'blue',    mandate: 'Safe beds, safe discharge.',  measuredOn: ['defect'] },
      { key: 'theatres',  name: 'Theatres',    variant: 'violet',  mandate: 'Keep the list running.',      measuredOn: ['inventory'] },
      { key: 'pharmacy',  name: 'Pharmacy',    variant: 'emerald', mandate: 'Right drug, right patient.',  measuredOn: ['defect', 'cost'] },
    ],
    kpis: [
      { key: 'downtime',    label: 'Wait time',         unit: 'minutes',       direction: 'lower',   cap: 240, normaliseAbs: false, variant: 'rose'    },
      { key: 'defect',      label: 'Safety incidents',  unit: 'count',         direction: 'lower',   cap: 20,  normaliseAbs: false, variant: 'amber'   },
      { key: 'inventory', label: 'Theatre use',       unit: 'percent',       direction: 'higher',  cap: 100, normaliseAbs: false, variant: 'violet'  },
      { key: 'cost',        label: 'Cost',              unit: "₹ '000",        direction: 'lower',   cap: 200, normaliseAbs: false, variant: 'slate'   },
      { key: 'custSat', label: 'Staff morale',      unit: 'points',        direction: 'higher',  cap: 20,  normaliseAbs: false, variant: 'emerald' },
    ],
  },
  {
    key: 'banking',
    label: 'Banking / Financial services',
    functionNoun: 'Function',
    eventDeckLabel: 'Market & Regulator Pressure',
    functions: [
      { key: 'sales',      name: 'Relationship', variant: 'blue',    mandate: 'Grow the book.',            measuredOn: ['downtime'] },
      { key: 'risk',       name: 'Risk',         variant: 'rose',    mandate: 'Keep losses off the book.', measuredOn: ['defect'] },
      { key: 'compliance', name: 'Compliance',   variant: 'violet',  mandate: 'Stay inside the rules.',    measuredOn: ['inventory'] },
      { key: 'operations', name: 'Operations',   variant: 'emerald', mandate: 'Process it accurately.',    measuredOn: ['cost'] },
    ],
    kpis: [
      { key: 'downtime',      label: 'Revenue',          unit: "₹ '00,000", direction: 'higher', cap: 200, normaliseAbs: false, variant: 'emerald' },
      { key: 'defect', label: 'Risk exposure',    unit: 'points',    direction: 'lower',  cap: 50,  normaliseAbs: false, variant: 'rose'    },
      { key: 'inventory',     label: 'Compliance breaches', unit: 'count',  direction: 'lower',  cap: 15,  normaliseAbs: false, variant: 'amber'   },
      { key: 'cost',    label: 'Cycle time',       unit: 'hours',     direction: 'lower',  cap: 72,  normaliseAbs: false, variant: 'violet'  },
      { key: 'custSat',      label: 'Client trust',     unit: 'points',    direction: 'higher', cap: 20,  normaliseAbs: false, variant: 'blue'    },
    ],
  },
  {
    key: 'software',
    label: 'Software / Technology',
    functionNoun: 'Team',
    eventDeckLabel: 'Customer & Market Pressure',
    functions: [
      { key: 'product',     name: 'Product',     variant: 'blue',    mandate: 'Ship what customers asked for.', measuredOn: ['downtime'] },
      { key: 'engineering', name: 'Engineering', variant: 'violet',  mandate: 'Keep the system standing.',      measuredOn: ['defect'] },
      { key: 'qa',          name: 'QA',          variant: 'emerald', mandate: 'Nothing broken reaches users.',  measuredOn: ['inventory'] },
      { key: 'support',     name: 'Support',     variant: 'amber',   mandate: 'Keep customers whole.',          measuredOn: ['custSat'] },
    ],
    kpis: [
      { key: 'downtime',    label: 'Delivery pace',    unit: 'points',  direction: 'higher', cap: 60,  normaliseAbs: false, variant: 'blue'    },
      { key: 'defect', label: 'Incidents',        unit: 'count',   direction: 'lower',  cap: 20,  normaliseAbs: false, variant: 'rose'    },
      { key: 'inventory',      label: 'Escaped defects',  unit: 'count',   direction: 'lower',  cap: 30,  normaliseAbs: false, variant: 'amber'   },
      { key: 'cost',    label: 'Tech debt',        unit: 'points',  direction: 'lower',  cap: 40,  normaliseAbs: false, variant: 'violet'  },
      { key: 'custSat',     label: 'Customer satisfaction', unit: 'points', direction: 'higher', cap: 20, normaliseAbs: false, variant: 'emerald' },
    ],
  },
];

/** Neutral fallback when the trainer's industry doesn't match a preset. */
export const GENERIC_PRESET = {
  key: 'generic',
  label: 'Something else',
  functionNoun: 'Team',
  eventDeckLabel: 'External Pressure',
  functions: [
    { key: 'team_a', name: 'Team A', variant: 'blue',    mandate: '', measuredOn: [] },
    { key: 'team_b', name: 'Team B', variant: 'violet',  mandate: '', measuredOn: [] },
    { key: 'team_c', name: 'Team C', variant: 'emerald', mandate: '', measuredOn: [] },
    { key: 'team_d', name: 'Team D', variant: 'amber',   mandate: '', measuredOn: [] },
  ],
  kpis: [
    { key: 'downtime',   label: 'Speed',                 unit: 'points', direction: 'higher',  cap: 50, normaliseAbs: false, variant: 'blue'    },
    { key: 'defect', label: 'Quality',               unit: 'points', direction: 'higher',  cap: 50, normaliseAbs: false, variant: 'emerald' },
    { key: 'inventory',    label: 'Cost',                  unit: "₹ '000", direction: 'lower',   cap: 200, normaliseAbs: false, variant: 'slate'  },
    { key: 'cost',    label: 'Risk',                  unit: 'points', direction: 'lower',   cap: 40, normaliseAbs: false, variant: 'rose'    },
    { key: 'custSat', label: 'Customer satisfaction', unit: 'points', direction: 'higher',  cap: 20, normaliseAbs: false, variant: 'amber'   },
  ],
};

export const presetFor = (key) =>
  INDUSTRY_PRESETS.find((p) => p.key === key) || GENERIC_PRESET;

export const AUDIENCE_LEVELS = [
  { key: 'frontline', label: 'Frontline / individual contributors' },
  { key: 'supervisor', label: 'Supervisors & team leads' },
  { key: 'middle', label: 'Middle management' },
  { key: 'senior', label: 'Senior leadership' },
  { key: 'mixed', label: 'Mixed levels' },
];

export default INDUSTRY_PRESETS;
